import React, { useState, useEffect } from 'react';
import { Configuration } from '../BackendApiClient/runtime';
import {
  ExperimentsApi,
  RegionsApi,
  DeviceInfosApi,
  ClassifiersApi,
  ProxiesApi,
  NarrativesApi,
} from '../BackendApiClient/apis';
import {
  RegionResponse,
  DeviceInfoResponse,
  ClassifierResponse,
  ProxyResponse,
  NarrativeResponse,
  ExperimentResponse,
} from '../BackendApiClient/models';
import { countryCodeToFlag, getProxyDisplayName } from '../utils/proxyDisplay';
import './ExperimentWizard.css';

interface BiasTestSchema {
  key: string;
  name: string;
  description: string;
  schema: {
    properties: Record<string, any>;
    required: string[];
    title: string;
    type: string;
  };
}

interface BiasTestFormData {
  type: string;
  params: Record<string, any>;
}

interface WizardStep {
  id: number;
  title: string;
  description: string;
}

interface RegionProxyConfig {
  regionId: number;
  proxyId: number | null;
  createMode: 'existing' | 'brightdata';
  brightDataName: string;
  brightDataCountry: string;
  brightDataIpCount: number;
}

const STEPS: WizardStep[] = [
  { id: 1, title: 'Experiment Info', description: 'Name and duration' },
  { id: 2, title: 'Bias Tests', description: 'Select and configure tests' },
  { id: 3, title: 'Review', description: 'Review and create' },
];

// Separate component for tag input to properly use hooks
interface TagInputProps {
  title: string;
  value: string[];
  onChange: (newValue: string[]) => void;
}

const TagInput: React.FC<TagInputProps> = ({ title, value, onChange }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (inputValue.trim()) {
      onChange([...(value || []), inputValue.trim()]);
      setInputValue('');
    }
  };

  return (
    <div className="tag-input-container">
      <div className="tags-display">
        {(value || []).map((tag: string, i: number) => (
          <span key={i} className="tag">
            {tag}
            <button
              className="tag-remove"
              onClick={() => {
                const newValue = value.filter((_: string, idx: number) => idx !== i);
                onChange(newValue);
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="tag-input-row">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && inputValue.trim()) {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={`Add ${title.toLowerCase()}...`}
          className="form-input"
        />
        <button
          type="button"
          onClick={handleAdd}
          className="btn btn-secondary btn-sm"
        >
          Add
        </button>
      </div>
    </div>
  );
};

// Component for region selection with proxy assignment
interface RegionProxySelectorProps {
  regions: RegionResponse[];
  proxies: ProxyResponse[];
  selectedRegions: RegionProxyConfig[];
  onChange: (configs: RegionProxyConfig[]) => void;
  ispCountries: { [key: string]: string | null };
  loadIspCountries: () => void;
  loadingCountries: boolean;
}

const RegionProxySelector: React.FC<RegionProxySelectorProps> = ({
  regions,
  proxies,
  selectedRegions,
  onChange,
  ispCountries,
  loadIspCountries,
  loadingCountries,
}) => {
  const [expandedRegion, setExpandedRegion] = useState<number | null>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);

  const toggleRegion = (regionId: number) => {
    const existing = selectedRegions.find((r) => r.regionId === regionId);
    if (existing) {
      onChange(selectedRegions.filter((r) => r.regionId !== regionId));
    } else {
      const region = regions.find((r) => r.regionId === regionId);
      onChange([
        ...selectedRegions,
        {
          regionId,
          proxyId: null,
          createMode: 'existing',
          brightDataName: region ? `${region.carrierRegion.toLowerCase()}_proxy_${Math.floor(Date.now() / 1000)}` : '',
          brightDataCountry: '',
          brightDataIpCount: 1,
        },
      ]);
    }
  };

  const updateRegionConfig = (regionId: number, updates: Partial<RegionProxyConfig>) => {
    onChange(
      selectedRegions.map((r) =>
        r.regionId === regionId ? { ...r, ...updates } : r
      )
    );
  };

  const getRegionConfig = (regionId: number) => {
    return selectedRegions.find((r) => r.regionId === regionId);
  };

  return (
    <div className="region-selector">
      <p className="form-help">Select regions for this test and configure their proxies</p>
      <div className="regions-list">
        {regions.map((region) => {
          const isSelected = !!getRegionConfig(region.regionId);
          const config = getRegionConfig(region.regionId);
          const isExpanded = expandedRegion === region.regionId;

          return (
            <div key={region.regionId} className={`region-item ${isSelected ? 'selected' : ''}`}>
              <div className="region-item-header">
                <label className="region-checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleRegion(region.regionId)}
                  />
                  <span className="region-label">
                    <span className="region-flag">{countryCodeToFlag(region.carrierRegion)}</span>
                    <span className="region-name">{region.carrierRegion}</span>
                    <span className="region-carrier">{region.carrierName}</span>
                  </span>
                </label>
                {isSelected && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setExpandedRegion(isExpanded ? null : region.regionId)}
                  >
                    {isExpanded ? '▲ Hide Proxy' : '▼ Configure Proxy'}
                  </button>
                )}
              </div>

              {isSelected && isExpanded && config && (
                <div className="region-proxy-config">
                  <div className="proxy-mode-selector">
                    <label className={`proxy-mode-option ${config.createMode === 'existing' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name={`proxy-mode-${region.regionId}`}
                        checked={config.createMode === 'existing'}
                        onChange={() => updateRegionConfig(region.regionId, { createMode: 'existing' })}
                      />
                      Use Existing Proxy
                    </label>
                    <label className={`proxy-mode-option ${config.createMode === 'brightdata' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name={`proxy-mode-${region.regionId}`}
                        checked={config.createMode === 'brightdata'}
                        onChange={() => {
                          updateRegionConfig(region.regionId, { createMode: 'brightdata' });
                          if (Object.keys(ispCountries).length === 0) {
                            loadIspCountries();
                          }
                        }}
                      />
                      Create BrightData ISP
                    </label>
                  </div>

                  {config.createMode === 'existing' ? (
                    <div className="form-group">
                      <label className="form-label">Select Proxy</label>
                      <select
                        value={config.proxyId || ''}
                        onChange={(e) =>
                          updateRegionConfig(region.regionId, {
                            proxyId: e.target.value ? parseInt(e.target.value) : null,
                          })
                        }
                        className="form-select"
                      >
                        <option value="">-- No Proxy --</option>
                        {proxies.map((proxy) => (
                          <option key={proxy.proxyId} value={proxy.proxyId}>
                            {getProxyDisplayName(proxy)}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="brightdata-config">
                      <div className="form-group">
                        <label className="form-label">Proxy Name *</label>
                        <input
                          type="text"
                          value={config.brightDataName}
                          onChange={(e) =>
                            updateRegionConfig(region.regionId, { brightDataName: e.target.value })
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Country *</label>
                        {loadingCountries ? (
                          <p className="loading-text">Loading countries...</p>
                        ) : (
                          <div className="country-picker">
                            <div
                              className="country-picker-toggle"
                              onClick={() => setCountryPickerOpen(!countryPickerOpen)}
                            >
                              {config.brightDataCountry ? (
                                <span>
                                  {countryCodeToFlag(config.brightDataCountry)} {Object.entries(ispCountries).find(([_, code]) => code === config.brightDataCountry)?.[0]} ({config.brightDataCountry})
                                </span>
                              ) : (
                                <span className="placeholder">Select a country...</span>
                              )}
                              <span className={`arrow ${countryPickerOpen ? 'open' : ''}`}>▼</span>
                            </div>
                            {countryPickerOpen && (
                              <div className="country-picker-dropdown">
                                <input
                                  type="text"
                                  value={countrySearch}
                                  onChange={(e) => setCountrySearch(e.target.value)}
                                  placeholder="🔍 Search countries..."
                                  className="form-input"
                                  autoFocus
                                />
                                <div className="country-list">
                                  {Object.entries(ispCountries)
                                    .filter(([name, code]) =>
                                      name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                      (code && code.toLowerCase().includes(countrySearch.toLowerCase()))
                                    )
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([name, code]) => (
                                      <div
                                        key={code}
                                        className={`country-option ${config.brightDataCountry === code ? 'selected' : ''}`}
                                        onClick={() => {
                                          updateRegionConfig(region.regionId, { brightDataCountry: code || '' });
                                          setCountryPickerOpen(false);
                                          setCountrySearch('');
                                        }}
                                      >
                                        <span>{countryCodeToFlag(code)} {name}</span>
                                        <span className="country-code">{code}</span>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Number of IPs *</label>
                        <input
                          type="number"
                          min={1}
                          max={100}
                          value={config.brightDataIpCount}
                          onChange={(e) =>
                            updateRegionConfig(region.regionId, {
                              brightDataIpCount: parseInt(e.target.value) || 1,
                            })
                          }
                          className="form-input"
                        />
                        <p className="form-help">$2/IP/month</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ExperimentWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // API clients
  const apiConfig = new Configuration({ basePath: '' });
  const experimentsApi = new ExperimentsApi(apiConfig);
  const regionsApi = new RegionsApi(apiConfig);
  const devicesApi = new DeviceInfosApi(apiConfig);
  const classifiersApi = new ClassifiersApi(apiConfig);
  const proxiesApi = new ProxiesApi(apiConfig);
  const narrativesApi = new NarrativesApi(apiConfig);

  // Available data
  const [biasTests, setBiasTests] = useState<BiasTestSchema[]>([]);
  const [regions, setRegions] = useState<RegionResponse[]>([]);
  const [devices, setDevices] = useState<DeviceInfoResponse[]>([]);
  const [classifiers, setClassifiers] = useState<ClassifierResponse[]>([]);
  const [proxies, setProxies] = useState<ProxyResponse[]>([]);
  const [narratives, setNarratives] = useState<NarrativeResponse[]>([]);
  const [experiments, setExperiments] = useState<ExperimentResponse[]>([]);
  const [ispCountries, setIspCountries] = useState<{ [key: string]: string | null }>({});
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Form state
  const [experimentMode, setExperimentMode] = useState<'new' | 'existing'>('new');
  const [selectedExperiment, setSelectedExperiment] = useState<ExperimentResponse | null>(null);
  const [experimentName, setExperimentName] = useState('');
  const [durationDays, setDurationDays] = useState(2);
  const [useSleepScheduling, setUseSleepScheduling] = useState(true);
  const [selectedBiasTests, setSelectedBiasTests] = useState<BiasTestFormData[]>([]);
  const [activeTestIndex, setActiveTestIndex] = useState<number | null>(null);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [biasTestsRes, regionsRes, devicesRes, classifiersRes, proxiesRes, narrativesRes, experimentsRes] = await Promise.all([
          experimentsApi.listBiasTestsApiExperimentsBiasTestsGet({}),
          regionsApi.getRegionsApiRegionsGet({}),
          devicesApi.getDeviceInfosApiDeviceInfosGet({}),
          classifiersApi.getClassifiersApiClassifiersGet({}),
          proxiesApi.getProxiesApiProxiesGet({}),
          narrativesApi.listNarrativesApiNarrativesGet(),
          experimentsApi.listExperimentsApiExperimentsGet(),
        ]);
        setBiasTests(biasTestsRes as BiasTestSchema[]);
        setRegions(regionsRes);
        setDevices(devicesRes);
        setClassifiers(classifiersRes);
        setProxies(proxiesRes);
        setNarratives(narrativesRes as NarrativeResponse[]);
        setExperiments(experimentsRes as ExperimentResponse[]);
      } catch (err) {
        setError('Failed to load data. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const loadIspCountries = async () => {
    setLoadingCountries(true);
    try {
      const countries = await proxiesApi.getBrightDataIspCountriesApiProxiesIspCountriesGet({});
      setIspCountries(countries);
    } catch (err) {
      console.error('Failed to load ISP countries:', err);
    } finally {
      setLoadingCountries(false);
    }
  };

  const addBiasTest = (testKey: string) => {
    const test = biasTests.find((t) => t.key === testKey);
    if (!test) return;

    // Initialize params with appropriate defaults
    const params: Record<string, any> = {};
    Object.entries(test.schema.properties).forEach(([key, prop]: [string, any]) => {
      if (key === 'region_id_to_proxy_id') {
        params[key] = {}; // Will be populated from RegionProxyConfig
      } else if (key === 'device_info_ids') {
        params[key] = [];
      } else if (key === 'classifier_id') {
        params[key] = null;
      } else if (key === 'bots_number_per_region' || key === 'bots_number_per_narrative_switch') {
        params[key] = 20; // Default to 20 bots per region
      } else if (prop.type === 'array') {
        params[key] = [];
      } else if (prop.type === 'integer') {
        params[key] = prop.default ?? 1;
      } else {
        params[key] = '';
      }
    });

    // Add internal state for region proxy configs
    params._regionConfigs = [];

    setSelectedBiasTests([...selectedBiasTests, { type: testKey, params }]);
    setActiveTestIndex(selectedBiasTests.length);
  };

  const removeBiasTest = (index: number) => {
    setSelectedBiasTests(selectedBiasTests.filter((_, i) => i !== index));
    if (activeTestIndex === index) {
      setActiveTestIndex(null);
    } else if (activeTestIndex !== null && activeTestIndex > index) {
      setActiveTestIndex(activeTestIndex - 1);
    }
  };

  const updateBiasTestParams = (index: number, key: string, value: any) => {
    const updated = [...selectedBiasTests];
    updated[index].params[key] = value;

    // If updating region configs, also update the region_id_to_proxy_id
    if (key === '_regionConfigs') {
      const regionProxyMap: Record<number, number | null> = {};
      (value as RegionProxyConfig[]).forEach((config) => {
        if (config.createMode === 'brightdata') {
          // Mark as needing creation (we'll handle this during submission)
          regionProxyMap[config.regionId] = -1; // Placeholder
        } else {
          // Include region even with null proxy
          regionProxyMap[config.regionId] = config.proxyId;
        }
      });
      updated[index].params.region_id_to_proxy_id = regionProxyMap;
    }

    setSelectedBiasTests(updated);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, create any BrightData proxies that were requested
      for (const biasTest of selectedBiasTests) {
        const regionConfigs = biasTest.params._regionConfigs as RegionProxyConfig[];
        const updatedRegionProxyMap: Record<number, number | null> = {};

        for (const config of regionConfigs) {
          if (config.createMode === 'brightdata' && config.brightDataName && config.brightDataCountry) {
            // Confirm cost
            const totalCost = config.brightDataIpCount * 2;
            const confirmed = window.confirm(
              `Creating BrightData ISP proxy for ${regions.find(r => r.regionId === config.regionId)?.carrierRegion}:\n\n` +
              `${config.brightDataIpCount} IP(s) × $2/IP/month = $${totalCost}/month\n\n` +
              `Proceed?`
            );
            if (!confirmed) {
              setLoading(false);
              return;
            }

            // Create the proxy
            const newProxy = await proxiesApi.createBrightDataIspProxyApiProxiesBrightDataIspCreatePost({
              createBrightDataISPProxyRequest: {
                proxyName: config.brightDataName,
                countryCode: config.brightDataCountry,
                ipCount: config.brightDataIpCount,
              },
            });
            updatedRegionProxyMap[config.regionId] = newProxy.proxyId;
          } else {
            // Include region with existing proxy or null
            updatedRegionProxyMap[config.regionId] = config.proxyId;
          }
        }

        // Update the region_id_to_proxy_id with created proxies
        biasTest.params.region_id_to_proxy_id = updatedRegionProxyMap;
      }

      // Clean up internal state before sending
      const cleanedBiasTests = selectedBiasTests.map((bt) => ({
        type: bt.type,
        params: Object.fromEntries(
          Object.entries(bt.params).filter(([key]) => !key.startsWith('_'))
        ),
      }));

      if (experimentMode === 'new') {
        // Create new experiment
        const response = await experimentsApi.createExperimentApiExperimentsPost({
          experimentCreate: {
            name: experimentName,
            durationSeconds: durationDays * 24 * 60 * 60,
            biasTests: cleanedBiasTests,
            useSleepSchedule: useSleepScheduling,
          },
        });

        setSuccess(
          `Experiment "${response.experimentName}" created successfully with ${response.groupIds?.length || 0} groups!`
        );
      } else if (selectedExperiment) {
        // Add bias tests to existing experiment
        const response = await fetch(`/api/experiments/${selectedExperiment.experimentId}/bias-tests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            bias_tests: cleanedBiasTests,
            use_sleep_schedule: useSleepScheduling 
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to add bias tests to experiment');
        }

        const result = await response.json();
        setSuccess(
          `Successfully added ${result.group_ids?.length || 0} new groups to experiment "${selectedExperiment.experimentName}"!`
        );
      }

      // Reset form
      setExperimentName('');
      setDurationDays(2);
      setSelectedBiasTests([]);
      setCurrentStep(1);
      setExperimentMode('new');
      setSelectedExperiment(null);
    } catch (err: any) {
      const message = err?.body?.detail || err?.message || 'Failed to create experiment';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        if (experimentMode === 'new') {
          return experimentName.trim().length > 0 && durationDays > 0;
        } else {
          return selectedExperiment !== null;
        }
      case 2:
        // Bias tests are required (they define the groups to create)
        return selectedBiasTests.length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const renderFieldInput = (
    testIndex: number,
    fieldName: string,
    fieldSchema: any,
    value: any
  ) => {
    const title = fieldSchema.title || fieldName;
    const currentTest = selectedBiasTests[testIndex];

    // Region to Proxy mapping - use our custom component
    if (fieldName === 'region_id_to_proxy_id') {
      const regionConfigs = currentTest.params._regionConfigs || [];
      return (
        <div className="form-group" key={fieldName}>
          <label className="form-label">{title}</label>
          <RegionProxySelector
            regions={regions}
            proxies={proxies}
            selectedRegions={regionConfigs}
            onChange={(configs) => updateBiasTestParams(testIndex, '_regionConfigs', configs)}
            ispCountries={ispCountries}
            loadIspCountries={loadIspCountries}
            loadingCountries={loadingCountries}
          />
        </div>
      );
    }

    // Device info IDs - multi-select
    if (fieldName === 'device_info_ids') {
      return (
        <div className="form-group" key={fieldName}>
          <label className="form-label">{title}</label>
          <p className="form-help">Select device profiles for the bots</p>
          <div className="device-grid">
            {devices.map((device) => {
              const isSelected = (value || []).includes(device.deviceInfoId);
              return (
                <div
                  key={device.deviceInfoId}
                  className={`device-card selectable ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    const current = value || [];
                    const newValue = isSelected
                      ? current.filter((id: number) => id !== device.deviceInfoId)
                      : [...current, device.deviceInfoId];
                    updateBiasTestParams(testIndex, fieldName, newValue);
                  }}
                >
                  <div className="device-icon">📱</div>
                  <div className="device-info">
                    <span className="device-name">
                      {device.deviceBrand} {device.devicePlatform}
                    </span>
                    <span className="device-detail">API {device.deviceOsApi}</span>
                  </div>
                  {isSelected && <span className="check-icon">✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Classifier ID - professional selector with parameter preview
    if (fieldName === 'classifier_id') {
      return (
        <>
          <div className="form-group" key={fieldName}>
            <label className="form-label">{title} (Optional)</label>
            <div className="classifier-selector">
              {/* Selection chips row */}
              <div className="classifier-chips">
                <button
                  type="button"
                  className={`classifier-chip ${value === null ? 'selected' : ''}`}
                  onClick={() => updateBiasTestParams(testIndex, fieldName, null)}
                >
                  <span className="chip-indicator"></span>
                  <span className="chip-label">None</span>
                </button>
                {classifiers.map((c) => (
                  <button
                    key={c.classifierId}
                    type="button"
                    className={`classifier-chip ${value === c.classifierId ? 'selected' : ''}`}
                    onClick={() => updateBiasTestParams(testIndex, fieldName, c.classifierId)}
                  >
                    <span className="chip-indicator"></span>
                    <span className="chip-label">{c.classifierType}</span>
                    <span className="chip-id">#{c.classifierId}</span>
                  </button>
                ))}
              </div>

              {/* Parameter preview panel */}
              <div className="classifier-preview-panel">
                {value === null ? (
                  <div className="preview-empty">
                    <span className="preview-empty-icon">○</span>
                    <span>No classifier selected — videos won't be analyzed</span>
                  </div>
                ) : (
                  (() => {
                    const selected = classifiers.find((c) => c.classifierId === value);
                    return selected ? (
                      <div className="preview-content">
                        <div className="preview-header">
                          <span className="preview-type">{selected.classifierType}</span>
                          <span className="preview-badge">ID: {selected.classifierId}</span>
                        </div>
                        <div className="preview-params">
                          <span className="preview-params-label">Configuration</span>
                          <pre className="preview-params-code">
                            {JSON.stringify(selected.classifierParams, null, 2)}
                          </pre>
                        </div>
                      </div>
                    ) : null;
                  })()
                )}
              </div>
            </div>
          </div>

          {/* Narrative Mappings Selection */}
          {narratives.length > 0 && (
            <div key="narrative_mapping_ids" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '300px', overflowY: 'auto', padding: '0.5rem' }}>
              {narratives.map(narrative => {
                const currentNarratives = currentTest.params.narrative_mapping_ids || [];
                const isSelected = currentNarratives.includes(narrative.narrativeId);
                return (
                  <label
                    key={narrative.narrativeId}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '0.75rem', 
                      cursor: 'pointer',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        const updated = isSelected
                          ? currentNarratives.filter((id: number) => id !== narrative.narrativeId)
                          : [...currentNarratives, narrative.narrativeId];
                        updateBiasTestParams(testIndex, 'narrative_mapping_ids', updated);
                      }}
                      style={{ marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500' }}>{narrative.name}</div>
                      {narrative.description && (
                        <div className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {narrative.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.375rem' }}>
                        <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>ID: {narrative.narrativeId}</span>
                        <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>v{narrative.version}</span>
                        <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>{narrative.modelName}</span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </>
      );
    }

    // String array - tag input
    if (fieldSchema.type === 'array' && fieldSchema.items?.type === 'string') {
      return (
        <div className="form-group" key={fieldName}>
          <label className="form-label">{title}</label>
          <TagInput
            title={title}
            value={value || []}
            onChange={(newValue) => updateBiasTestParams(testIndex, fieldName, newValue)}
          />
        </div>
      );
    }

    // Integer input
    if (fieldSchema.type === 'integer') {
      return (
        <div className="form-group" key={fieldName}>
          <label className="form-label">{title}</label>
          <input
            type="number"
            value={value || ''}
            min={fieldSchema.minimum || fieldSchema.ge || 1}
            max={fieldSchema.maximum || fieldSchema.le || 100}
            onChange={(e) => {
              updateBiasTestParams(testIndex, fieldName, parseInt(e.target.value) || 0);
            }}
            className="form-input"
          />
          {(fieldSchema.ge || fieldSchema.le) && (
            <p className="form-help">
              Range: {fieldSchema.ge || 1} - {fieldSchema.le || 100}
            </p>
          )}
        </div>
      );
    }

    // Default text input
    return (
      <div className="form-group" key={fieldName}>
        <label className="form-label">{title}</label>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => updateBiasTestParams(testIndex, fieldName, e.target.value)}
          className="form-input"
        />
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="step-content">
      <h2>Experiment Setup</h2>
      <p className="text-muted">Configure or select an experiment</p>

      <div className="form-section">
        <div className="form-group">
          <label className="form-label">Experiment Mode</label>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={experimentMode === 'new'}
                onChange={() => setExperimentMode('new')}
                style={{ width: '18px', height: '18px' }}
              />
              <span>Create New Experiment</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={experimentMode === 'existing'}
                onChange={() => setExperimentMode('existing')}
                style={{ width: '18px', height: '18px' }}
              />
              <span>Add to Existing Experiment</span>
            </label>
          </div>
        </div>

        {experimentMode === 'existing' ? (
          <div className="form-group">
            <label className="form-label">Select Experiment</label>
            <select
              value={selectedExperiment?.experimentId || ''}
              onChange={e => {
                const exp = experiments.find(ex => ex.experimentId === Number(e.target.value));
                setSelectedExperiment(exp || null);
              }}
              className="form-input"
            >
              <option value="">-- Select an experiment --</option>
              {experiments.map(exp => (
                <option key={exp.experimentId} value={exp.experimentId}>
                  {exp.experimentName} (ID: {exp.experimentId})
                </option>
              ))}
            </select>
            {selectedExperiment && (
              <p className="form-help">
                New groups will be added to: {selectedExperiment.experimentName}
              </p>
            )}
            {experiments.length === 0 && (
              <p className="form-help" style={{ color: 'var(--accent-yellow)' }}>
                No experiments available. Switch to "Create New Experiment" mode.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label">Experiment Name</label>
              <input
                type="text"
                value={experimentName}
                onChange={(e) => setExperimentName(e.target.value)}
                placeholder="e.g., Q1 Regional Bias Study"
                className="form-input form-input-lg"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Duration (Days)</label>
              <div className="duration-input">
                <input
                  type="number"
                  value={durationDays}
                  onChange={(e) => setDurationDays(parseInt(e.target.value) || 1)}
                  min={1}
                  max={30}
                  className="form-input"
                />
                <span className="duration-label">days</span>
              </div>
              <p className="form-help">
                The experiment will run for {durationDays} day{durationDays > 1 ? 's' : ''} (
                {durationDays * 24} hours)
              </p>
            </div>

            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={useSleepScheduling}
                  onChange={(e) => setUseSleepScheduling(e.target.checked)}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <div>
                  <span style={{ fontWeight: '500', fontSize: '1rem' }}>🌙 Automatic Sleep Scheduling</span>
                  <p className="form-help" style={{ margin: '0.25rem 0 0 0' }}>
                    Bots will automatically sleep during night hours (00:00-07:00) based on their region's timezone
                  </p>
                </div>
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <h2>Configure Bias Tests</h2>
      <p className="text-muted">Select and configure the tests to run in this experiment</p>

      {experimentMode === 'existing' && (
        <div style={{ 
          background: 'var(--bg-secondary)', 
          padding: '1rem', 
          borderRadius: '8px', 
          marginBottom: '1.5rem',
          border: '1px solid var(--border-default)'
        }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            ℹ️ <strong>Note:</strong> You are adding to an existing experiment. 
            Bias tests are already configured for "{selectedExperiment?.experimentName}". 
            You can skip this step or proceed directly to review.
          </p>
        </div>
      )}

      <div className="bias-tests-layout">
        {/* Test Selector */}
        <div className="test-selector-section">
          <h3>Available Tests</h3>
          <div className="test-cards">
            {biasTests.map((test) => (
              <div key={test.key} className="test-card" onClick={() => addBiasTest(test.key)}>
                <div className="test-card-icon">{test.key === 'topic_search' ? '🔍' : '🔄'}</div>
                <div className="test-card-content">
                  <h4>{test.name}</h4>
                  <p>{test.description}</p>
                </div>
                <button className="btn btn-primary btn-sm">+ Add</button>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Tests */}
        <div className="selected-tests-section">
          <h3>Selected Tests ({selectedBiasTests.length})</h3>

          {selectedBiasTests.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📋</span>
              <p>No tests selected yet</p>
              <span className="empty-hint">Click on a test above to add it</span>
            </div>
          ) : (
            <div className="selected-tests-list">
              {selectedBiasTests.map((test, index) => {
                const testSchema = biasTests.find((t) => t.key === test.type);
                const isActive = activeTestIndex === index;

                return (
                  <div key={index} className={`selected-test-item ${isActive ? 'active' : ''}`}>
                    <div
                      className="selected-test-header"
                      onClick={() => setActiveTestIndex(isActive ? null : index)}
                    >
                      <span className="test-icon">
                        {test.type === 'topic_search' ? '🔍' : '🔄'}
                      </span>
                      <span className="test-name">{testSchema?.name || test.type}</span>
                      <div className="test-actions">
                        <span className="expand-icon">{isActive ? '▼' : '▶'}</span>
                        <button
                          className="btn-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeBiasTest(index);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    {isActive && testSchema && (
                      <div className="selected-test-form">
                        {Object.entries(testSchema.schema.properties)
                          .filter(([key]) => !key.startsWith('_'))
                          .map(([fieldName, fieldSchema]) =>
                            renderFieldInput(index, fieldName, fieldSchema, test.params[fieldName])
                          )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <h2>Review & Create</h2>
      <p className="text-muted">Review your experiment configuration before creating</p>

      <div className="review-section">
        <div className="review-card">
          <h3>Experiment Details</h3>
          <div className="review-row">
            <span className="review-label">Name</span>
            <span className="review-value">{experimentName}</span>
          </div>
          <div className="review-row">
            <span className="review-label">Duration</span>
            <span className="review-value">
              {durationDays} days ({durationDays * 24} hours)
            </span>
          </div>
        </div>

        <div className="review-card">
          <h3>Bias Tests ({selectedBiasTests.length})</h3>
          {selectedBiasTests.map((test, index) => {
            const testSchema = biasTests.find((t) => t.key === test.type);
            const regionConfigs = (test.params._regionConfigs as RegionProxyConfig[]) || [];
            const regionCount = regionConfigs.length;
            const botsPerRegion =
              test.params.bots_number_per_region ||
              test.params.bots_number_per_narrative_switch ||
              0;

            return (
              <div key={index} className="review-test">
                <div className="review-test-header">
                  <span className="test-icon">{test.type === 'topic_search' ? '🔍' : '🔄'}</span>
                  <span className="test-name">{testSchema?.name}</span>
                </div>
                <div className="review-test-stats">
                  <div className="stat">
                    <span className="stat-value">{regionCount}</span>
                    <span className="stat-label">Regions</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{botsPerRegion}</span>
                    <span className="stat-label">Bots/Region</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">
                      {(test.params.topics || test.params.narrative_topics || []).length}
                    </span>
                    <span className="stat-label">Topics</span>
                  </div>
                </div>
                {regionConfigs.length > 0 && (
                  <div className="review-regions">
                    <strong>Regions:</strong>
                    <div className="region-tags">
                      {regionConfigs.map((rc) => {
                        const region = regions.find((r) => r.regionId === rc.regionId);
                        return (
                          <span key={rc.regionId} className="region-tag">
                            🌍 {region?.carrierRegion}
                            {rc.createMode === 'brightdata' && (
                              <span className="new-proxy-badge">+ New Proxy</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="review-summary">
          <div className="summary-icon">🚀</div>
          <p>
            This experiment will create bot groups across all configured regions and start running
            the specified bias tests for {durationDays} days.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="experiment-wizard-container">
      {/* Progress Steps */}
      <div className="wizard-steps">
        {STEPS.map((step) => (
          <div
            key={step.id}
            className={`wizard-step ${currentStep === step.id ? 'active' : ''} ${
              currentStep > step.id ? 'completed' : ''
            }`}
            onClick={() => step.id < currentStep && setCurrentStep(step.id)}
          >
            <div className="step-number">{currentStep > step.id ? '✓' : step.id}</div>
            <div className="step-info">
              <span className="step-title">{step.title}</span>
              <span className="step-description">{step.description}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Status Messages */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="alert-close">
            ×
          </button>
        </div>
      )}

      {/* Content */}
      <div className="wizard-content">
        {loading && !biasTests.length ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading experiment configuration...</p>
          </div>
        ) : (
          <>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="wizard-navigation">
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentStep(currentStep - 1)}
          disabled={currentStep === 1 || loading}
        >
          ← Back
        </button>

        <div className="nav-spacer" />

        {currentStep < 3 ? (
          <button
            className="btn btn-primary"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed() || loading}
          >
            Continue →
          </button>
        ) : (
          <button
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={!canProceed() || loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner small"></span>
                Creating...
              </>
            ) : (
              <>🚀 Create Experiment</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExperimentWizard;
